import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { ActivityAudioEngine } from './audio-engine';
import type { SoundName } from './sounds';

const STORAGE_PREFIX = 'runhuman.activity.muted';

interface ActivityAudioValue {
  readonly muted: boolean;
  readonly setMuted: (muted: boolean) => void;
  readonly play: (sound: SoundName) => void;
}

const ActivityAudioContext = createContext<ActivityAudioValue | null>(null);

/**
 * Read the persisted mute preference for `discordUserId` from localStorage.
 *
 * Keyed on the Discord user id so multi-account testers don't fight over a
 * single global flag (#3972). Returns `false` (unmuted) on any miss — the
 * mute toggle is opt-in.
 */
function readPersistedMute(discordUserId: string): boolean {
  if (typeof window === 'undefined') return false;
  const raw = window.localStorage.getItem(`${STORAGE_PREFIX}:${discordUserId}`);
  return raw === '1';
}

function writePersistedMute(discordUserId: string, muted: boolean): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(`${STORAGE_PREFIX}:${discordUserId}`, muted ? '1' : '0');
}

interface ProviderProps {
  readonly discordUserId: string;
  readonly children: ReactNode;
}

/**
 * Owns the singleton `ActivityAudioEngine` for the lifetime of the authorized
 * session and exposes `{ play, muted, setMuted }` to descendants.
 *
 * Mounting:
 *
 *  - One provider per linked-user session, instantiated by `<AuthorizedView>`.
 *  - The engine is created lazily inside the provider so unauthorized /
 *    unlinked viewers never spin up an AudioContext.
 *  - `discordUserId` keys both the engine instance and the localStorage slot —
 *    if the linked user changes (rare, but happens during preview-harness
 *    role-flipping), we tear down the old engine and read a fresh muted state.
 */
export function ActivityAudioProvider({ discordUserId, children }: ProviderProps) {
  const [muted, setMutedState] = useState(() => readPersistedMute(discordUserId));
  const engineRef = useRef<ActivityAudioEngine | null>(null);

  if (engineRef.current === null) {
    engineRef.current = new ActivityAudioEngine({ initiallyMuted: muted });
  }

  // Re-key the engine + muted state when `discordUserId` changes. Reading
  // localStorage inside the effect is intentional — `useState`'s initializer
  // captured the prior id's value, so we re-pull on switch.
  useEffect(() => {
    const persisted = readPersistedMute(discordUserId);
    setMutedState(persisted);
    engineRef.current?.setMuted(persisted);
  }, [discordUserId]);

  // Close the AudioContext on unmount so Safari doesn't leak suspended
  // contexts when the iframe re-mounts (Activity re-launch).
  useEffect(() => {
    return () => {
      void engineRef.current?.close();
      engineRef.current = null;
    };
  }, []);

  const setMuted = useCallback(
    (next: boolean) => {
      setMutedState(next);
      engineRef.current?.setMuted(next);
      writePersistedMute(discordUserId, next);
    },
    [discordUserId],
  );

  const play = useCallback((sound: SoundName) => {
    engineRef.current?.play(sound);
  }, []);

  const value = useMemo<ActivityAudioValue>(
    () => ({ muted, setMuted, play }),
    [muted, setMuted, play],
  );

  return <ActivityAudioContext.Provider value={value}>{children}</ActivityAudioContext.Provider>;
}

export function useActivityAudio(): ActivityAudioValue {
  const ctx = useContext(ActivityAudioContext);
  if (!ctx) {
    throw new Error('useActivityAudio must be used inside <ActivityAudioProvider>');
  }
  return ctx;
}
